import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useSettings } from "@/contexts/SettingsContext";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { LogOut } from "lucide-react";

export default function Admin() {
    const { refresh } = useSettings();
    const { logout } = useAdminAuth();
    const [settings, setSettings] = useState(null);
    const [enquiries, setEnquiries] = useState([]);
    const [saving, setSaving] = useState(false);
    const [shopify, setShopify] = useState(null);
    const [job, setJob] = useState(null);

    useEffect(() => {
        api.get("/settings").then((r) => setSettings(r.data));
        api.get("/enquiries").then((r) => setEnquiries(r.data));
        api.get("/shopify/status")
            .then((r) => setShopify(r.data))
            .catch((err) => { console.warn("[Admin] shopify status check failed:", err); });
        api.get("/shopify/sync/status")
            .then((r) => setJob(r.data))
            .catch((err) => { console.warn("[Admin] sync status fetch failed:", err); });
    }, []);

    const on = (k) => (e) => setSettings((s) => ({ ...s, [k]: e.target.value }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...settings }; delete payload.id;
            await api.put("/settings", payload);
            await refresh();
            toast.success("Settings saved");
        } catch (e) { toast.error("Save failed"); }
        finally { setSaving(false); }
    };

    // Poll sync status while a job is running
    useEffect(() => {
        if (!job || (job.status !== "queued" && job.status !== "running")) return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get("/shopify/sync/status", { params: { job_id: job.id } });
                setJob(data);
                if (data.status === "success") {
                    clearInterval(t);
                    const r = data.result || {};
                    toast.success(`Synced ✓ ${r.imported} products, ${r.collections} collections (${r.inserted} new, ${r.updated} updated, ${r.removed} removed)`);
                } else if (data.status === "error") {
                    clearInterval(t);
                    toast.error(`Sync failed: ${data.error || "unknown error"}`);
                }
            } catch (err) { console.warn("[Admin] sync status poll failed:", err); }
        }, 1500);
        return () => clearInterval(t);
    }, [job?.id, job?.status]);

    const onSyncShopify = async () => {
        try {
            const { data } = await api.post("/shopify/sync");
            if (data.already_running) {
                toast.info("A sync is already running");
            } else {
                toast.success("Sync started — running in the background");
            }
            // Fetch full job record
            const { data: status } = await api.get("/shopify/sync/status", { params: { job_id: data.job_id } });
            setJob(status);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Failed to start sync");
        }
    };

    const isSyncing = job && (job.status === "queued" || job.status === "running");

    if (!settings) return <div className="text-center py-20 text-muted-ink">Loading…</div>;

    return (
        <div className="bg-paper min-h-screen">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-16">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="overline">Admin Console</p>
                        <h1 className="font-serif text-5xl text-ink mt-3">Site Settings</h1>
                        <p className="mt-3 text-charcoal text-sm">Manage your contact info, Shopify sync and review jeweller enquiries.</p>
                    </div>
                    <button
                        onClick={() => { logout(); toast.success("Signed out"); }}
                        data-testid="admin-logout"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-soft text-charcoal hover:border-ink text-[11px] uppercase tracking-[0.22em]"
                    >
                        <LogOut size={14} /> Sign out
                    </button>
                </div>

                <Tabs defaultValue="settings" className="mt-10">
                    <TabsList className="bg-ivory border border-soft">
                        <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
                        <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries ({enquiries.length})</TabsTrigger>
                    </TabsList>

                    <TabsContent value="settings" className="mt-8 space-y-8">
                        <div className="grid sm:grid-cols-2 gap-5 bg-ivory p-8">
                            <F label="WhatsApp number (digits only, e.g. 918058876405)"><Input value={settings.whatsapp_number} onChange={on('whatsapp_number')} data-testid="set-whatsapp" className="bg-white border-soft" /></F>
                            <F label="Phone (display)"><Input value={settings.phone_display} onChange={on('phone_display')} data-testid="set-phone" className="bg-white border-soft" /></F>
                            <F label="Email"><Input value={settings.email} onChange={on('email')} data-testid="set-email" className="bg-white border-soft" /></F>
                            <F label="Address"><Input value={settings.address} onChange={on('address')} data-testid="set-address" className="bg-white border-soft" /></F>
                            <F label="Business hours"><Input value={settings.business_hours} onChange={on('business_hours')} data-testid="set-hours" className="bg-white border-soft" /></F>
                            <F label="Instagram URL"><Input value={settings.instagram} onChange={on('instagram')} data-testid="set-ig" className="bg-white border-soft" /></F>
                            <div className="sm:col-span-2"><F label="Tagline"><Textarea value={settings.tagline} onChange={on('tagline')} data-testid="set-tagline" className="bg-white border-soft" /></F></div>
                        </div>
                        <button onClick={save} disabled={saving} data-testid="admin-save" className="px-8 py-3 bg-ink text-paper uppercase text-[11px] tracking-[0.28em] hover:bg-bronze-deep">
                            {saving ? "Saving…" : "Save Settings"}
                        </button>

                        <div className="bg-ivory p-8 border-t-2 border-bronze">
                            <p className="overline">Shopify Sync</p>
                            <h3 className="font-serif text-2xl text-ink mt-3">Import catalog from your Shopify store</h3>
                            <p className="text-xs text-charcoal mt-2 max-w-2xl leading-relaxed">
                                Pulls all products + collections from your Shopify store's public feed and upserts them into the catalog. Existing product URLs stay stable. Configured store: <code className="text-bronze-deep">{shopify?.shop?.domain || "—"}</code>
                            </p>
                            <p className="text-[11px] text-muted-ink mt-2">
                                ⏱ Auto-syncs nightly at 3:00 AM IST. You can also sync on-demand below.
                            </p>
                            <div className="mt-4 text-xs">
                                <span className="text-muted-ink">Connection: </span>
                                {shopify ? (
                                    shopify.ok ? <span className="text-bronze-deep" data-testid="shopify-status-ok">Connected · ready to sync</span>
                                        : <span className="text-red-700" data-testid="shopify-status-bad">Not connected ({shopify.http || shopify.reason || 'error'})</span>
                                ) : <span className="text-muted-ink">checking…</span>}
                            </div>

                            {job && (
                                <div className="mt-4 p-4 bg-paper border border-soft" data-testid="sync-job-status">
                                    <div className="flex items-center justify-between gap-3 text-xs">
                                        <div className="flex items-center gap-2">
                                            {isSyncing && <span className="inline-block w-2 h-2 bg-bronze rounded-full animate-pulse" />}
                                            <span className="uppercase tracking-[0.22em] text-muted-ink">Last sync:</span>
                                            <span className={`uppercase tracking-[0.22em] ${job.status === 'success' ? 'text-bronze-deep' : job.status === 'error' ? 'text-red-700' : 'text-ink'}`} data-testid="sync-job-state">
                                                {job.status === "running" ? "Running" : job.status === "queued" ? "Queued" : job.status === "success" ? "Success" : job.status === "error" ? "Failed" : job.status}
                                            </span>
                                            {job.triggered_by === "scheduled" && <span className="text-[10px] uppercase tracking-[0.18em] text-muted-ink">· nightly</span>}
                                        </div>
                                        {job.finished_at && <span className="text-muted-ink">{new Date(job.finished_at).toLocaleString()}</span>}
                                    </div>
                                    {isSyncing && job.progress && (
                                        <p className="mt-2 text-sm text-charcoal italic" data-testid="sync-progress">{job.progress}</p>
                                    )}
                                    {job.status === "success" && job.result && (
                                        <p className="mt-2 text-sm text-charcoal" data-testid="sync-result">
                                            <strong className="text-ink">{job.result.imported}</strong> products · <strong className="text-ink">{job.result.collections}</strong> collections
                                            <span className="text-muted-ink"> · {job.result.inserted} new, {job.result.updated} updated, {job.result.removed} removed</span>
                                        </p>
                                    )}
                                    {job.status === "error" && job.error && (
                                        <p className="mt-2 text-sm text-red-700" data-testid="sync-error">{job.error}</p>
                                    )}
                                </div>
                            )}

                            <button
                                onClick={onSyncShopify}
                                disabled={isSyncing}
                                data-testid="admin-shopify-sync"
                                className="mt-5 px-8 py-3 bg-bronze-deep text-paper uppercase text-[11px] tracking-[0.28em] hover:bg-ink disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSyncing ? "Syncing in background…" : "Sync products from Shopify"}
                            </button>
                        </div>
                    </TabsContent>

                    <TabsContent value="enquiries" className="mt-8 space-y-4">
                        {enquiries.length === 0 ? <p className="text-muted-ink text-sm">No enquiries yet.</p> : enquiries.map((e) => (
                            <div key={e.id} className="bg-ivory p-5" data-testid={`enquiry-${e.id}`}>
                                <div className="flex flex-wrap items-baseline justify-between gap-2">
                                    <p className="font-serif text-xl text-ink">{e.shop_name || "Anonymous"} {e.contact_name && `· ${e.contact_name}`}</p>
                                    <p className="text-xs text-muted-ink">{new Date(e.created_at).toLocaleString()}</p>
                                </div>
                                <p className="text-xs text-muted-ink mt-1">{[e.city, e.phone, e.email].filter(Boolean).join(" · ")}</p>
                                <ul className="mt-3 text-sm space-y-1">
                                    {e.items.map((it, i) => (
                                        <li key={`${e.id}-${it.sku || it.product_id || i}`} className="text-charcoal">— {it.name} <span className="text-muted-ink">(SKU {it.sku})</span> × {it.quantity}{it.notes ? ` · "${it.notes}"` : ""}</li>
                                    ))}
                                </ul>
                                {e.message && <p className="mt-3 text-sm text-muted-ink italic">"{e.message}"</p>}
                            </div>
                        ))}
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

const F = ({ label, children }) => (
    <div>
        <Label className="text-[10px] text-muted-ink uppercase tracking-[0.22em]">{label}</Label>
        <div className="mt-2">{children}</div>
    </div>
);
